import React, { PureComponent } from 'react';
import {
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Button,
  Grid,
  Typography,
  CircularProgress,
  Box,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import S3 from '../../aws';
import { getCurrentSession } from '../../dataModelHelpers';
import logger from '../../logger';
import { TeamFieldSet, RobotsFieldSet } from '@rogoag/airtable/crm';
import { LABEL_SERVER_HTTPS_URL } from '../../labelServerUtils';
import { FILE_RECOVERY_HTTPS_URL } from '../../recoveryServerUtils';
import { getLocalAtSearch } from '../../db_ops/airtable_search_ops';

interface TestResult {
  id: string;
  name: string;
  status: 'pending' | 'success' | 'failure';
  message?: string;
  details?: string;
}

type TestFunction = () => Promise<{ success: boolean; message?: string; details?: string }>;

interface TestDefinition {
  id: string;
  name: string;
  testFn: TestFunction;
}

interface InternetTestPopupProps {
  onClose: () => void;
}

interface InternetTestPopupState {
  tests: Map<string, TestResult>;
  running: boolean;
  currentTestElapsedTime: number;
}

export default class InternetTestPopup extends PureComponent<InternetTestPopupProps, InternetTestPopupState> {
  private testElapsedTimeInterval: NodeJS.Timeout | null = null;

  // Test definitions - add new tests here
  private testDefinitions: TestDefinition[] = [
    { id: 'public-ip', name: 'Get Public IP Address', testFn: () => this.testPublicIP() },
    { id: 'isp', name: 'Identify Internet Provider', testFn: () => this.testISP() },
    { id: 'aws-url', name: 'AWS Public URL Request', testFn: () => this.testAWSPublicURL() },
    { id: 'aws-upload', name: 'Upload File to AWS Bucket', testFn: () => this.testAWSUpload() },
    { id: 'airtable-query', name: 'Query Operator from Airtable', testFn: () => this.testAirtableQuery() },
    { id: 'update-operator', name: 'Update Operator Record in Airtable', testFn: () => this.testUpdateOperator() },
    { id: 'update-robot', name: 'Update Robot Record in Airtable', testFn: () => this.testUpdateRobot() },
    { id: 'label-server', name: 'Label Server Ping', testFn: () => this.testLabelServerPing() },
    { id: 'recovery-server', name: 'Recovery Server Ping (Hostname)', testFn: () => this.testRecoveryServerPing() },
  ];

  constructor(props: InternetTestPopupProps) {
    super(props);

    // Initialize tests map from definitions
    const tests = new Map<string, TestResult>();
    this.testDefinitions.forEach((def) => {
      tests.set(def.id, {
        id: def.id,
        name: def.name,
        status: 'pending',
      });
    });

    this.state = {
      tests,
      running: false,
      currentTestElapsedTime: 0,
    };
  }

  async componentDidMount() {
    await this.runTests();
  }

  componentWillUnmount() {
    if (this.testElapsedTimeInterval) {
      clearInterval(this.testElapsedTimeInterval);
    }
  }

  startTestTimer = () => {
    this.setState({ currentTestElapsedTime: 0 });

    if (this.testElapsedTimeInterval) {
      clearInterval(this.testElapsedTimeInterval);
    }

    this.testElapsedTimeInterval = setInterval(() => {
      this.setState((prev) => ({ currentTestElapsedTime: prev.currentTestElapsedTime + 0.1 }));
    }, 100);
  };

  stopTestTimer = () => {
    if (this.testElapsedTimeInterval) {
      clearInterval(this.testElapsedTimeInterval);
    }
  };

  updateTestStatus = (id: string, status: 'success' | 'failure', message?: string, details?: string) => {
    this.stopTestTimer();
    this.setState((prevState) => {
      const tests = new Map(prevState.tests);
      const test = tests.get(id);

      if (test) {
        tests.set(id, { ...test, status, message, details });
      }

      return { tests };
    });
  };

  // Generic fetch wrapper with timeout and error handling
  fetchWithTimeout = async (url: string, options: RequestInit = {}, timeoutMs: number = 10000): Promise<Response> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          ...options.headers,
        },
      });
      clearTimeout(timeoutId);

      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  };

  // Run a test and update status accordingly
  runTest = async (testDef: TestDefinition) => {
    this.startTestTimer();

    try {
      const result = await testDef.testFn();

      if (result.success) {
        this.updateTestStatus(testDef.id, 'success', result.message, result.details);
        logger.log(`Internet test "${testDef.name}" succeeded${result.message ? `: ${result.message}` : ''}`);
      } else {
        this.updateTestStatus(testDef.id, 'failure', result.message, result.details);
        logger.log(`Internet test "${testDef.name}" failed${result.message ? `: ${result.message}` : ''}`);
      }
    } catch (error: any) {
      const errorMessage = error.name === 'AbortError' ? 'Request timed out' : error.message || 'Unknown error';
      this.updateTestStatus(testDef.id, 'failure', errorMessage);
      logger.log(`Internet test "${testDef.name}" threw error: ${errorMessage}`);
    }
  };

  runTests = async () => {
    this.setState({ running: true });

    // Run all tests in sequence
    for (const testDef of this.testDefinitions) {
      await this.runTest(testDef);
    }

    this.setState({ running: false });
  };

  // ============ Test Methods ============
  // All test methods now return a result object instead of directly updating state

  testPublicIP = async (): Promise<{ success: boolean; message?: string; details?: string }> => {
    const response = await this.fetchWithTimeout('https://api.ipify.org?format=json', {}, 10000);

    if (!response.ok) {
      return { success: false, message: `HTTP ${response.status}` };
    }

    const data = await response.json();

    return { success: true, message: `IP: ${data.ip}` };
  };

  testISP = async (): Promise<{ success: boolean; message?: string; details?: string }> => {
    const response = await this.fetchWithTimeout('https://ipapi.co/json', {}, 10000);

    if (!response.ok) {
      return { success: false, message: `HTTP ${response.status}` };
    }

    const data = await response.json();
    const provider = data.org || data.isp || 'Unknown';

    return { success: true, message: `Provider: ${provider}` };
  };

  testAWSPublicURL = async (): Promise<{ success: boolean; message?: string; details?: string }> => {
    const testUrl = S3.urlFor('test.txt');
    const response = await this.fetchWithTimeout(testUrl, { method: 'HEAD' }, 10000);

    // We don't care if the file exists, we just care that we can reach AWS
    return { success: true, message: `AWS reachable (status: ${response.status})` };
  };

  testAWSUpload = async (): Promise<{ success: boolean; message?: string; details?: string }> => {
    const timestamp = new Date().toISOString();
    const session = getCurrentSession();
    const username = session?.username || 'unknown';
    const filename = `internet_test_${username}_${timestamp}.txt`;
    const content = `Internet test performed at ${timestamp} by ${username}`;
    const blob = new Blob([content], { type: 'text/plain' });

    const checksum = await S3.upload(filename, blob, 'text/plain', {
      bucket: 'airtableuploads',
    });

    if (!checksum) {
      return { success: false, message: 'Upload returned no checksum' };
    }

    return { success: true, message: 'File uploaded successfully' };
  };

  testAirtableQuery = async (): Promise<{ success: boolean; message?: string; details?: string }> => {
    const session = getCurrentSession();
    const user = session?.getUser();

    if (!user || !user.airtable_name) {
      return { success: false, message: 'No operator logged in or airtable_name not set' };
    }

    const search = await getLocalAtSearch<TeamFieldSet>('Team', 'Operator Login for App - LOCK', '');

    if (!search) {
      return { success: false, message: 'Could not create search object' };
    }

    await search.refresh({ waitForAttachments: false });
    const records = search.getRecords();

    const operatorRecord = records.find((record) => record.get('Name') === user.airtable_name);

    if (!operatorRecord) {
      return { success: false, message: `Operator record not found for ${user.airtable_name}` };
    }

    return { success: true, message: `Found operator: ${user.airtable_name}` };
  };

  testUpdateOperator = async (): Promise<{ success: boolean; message?: string; details?: string }> => {
    const session = getCurrentSession();
    const user = session?.getUser();

    if (!user || !user.airtable_name) {
      return { success: false, message: 'No operator logged in or airtable_name not set' };
    }

    const search = await getLocalAtSearch<TeamFieldSet>('Team', 'Operator Login for App - LOCK', '');

    if (!search) {
      return { success: false, message: 'Could not create search object' };
    }

    await search.refresh({ waitForAttachments: false });
    const records = search.getRecords();

    const operatorRecord = records.find((record) => record.get('Name') === user.airtable_name);

    if (!operatorRecord) {
      return { success: false, message: `Operator record not found for ${user.airtable_name}` };
    }

    const timestamp = new Date().toISOString();
    await operatorRecord.set('Last Internet Test', timestamp);
    await operatorRecord.sync();

    return { success: true, message: 'Updated operator record' };
  };

  testUpdateRobot = async (): Promise<{ success: boolean; message?: string; details?: string }> => {
    const session = getCurrentSession();
    const robot = session?.getRobot() ?? { hostname: 'K-1000' };

    if (!robot || !robot.hostname) {
      return { success: false, message: 'No robot connected or hostname not set' };
    }

    const search = await getLocalAtSearch<RobotsFieldSet>('Robot', 'All Robots', '');

    if (!search) {
      return { success: false, message: 'Could not create search object' };
    }

    await search.refresh({ waitForAttachments: false });
    const records = search.getRecords();

    const robotRecord = records.find((record) => record.get('Name') === robot.hostname);

    if (!robotRecord) {
      return { success: false, message: `Robot record not found for hostname ${robot.hostname}` };
    }

    const timestamp = new Date().toISOString();
    await robotRecord.set('Last Internet Test', timestamp);
    await robotRecord.sync();

    return { success: true, message: 'Updated robot record' };
  };

  testLabelServerPing = async (): Promise<{ success: boolean; message?: string; details?: string }> => {
    const startTime = performance.now();
    const url = `${LABEL_SERVER_HTTPS_URL()}/meta/ping`;
    const response = await this.fetchWithTimeout(url, { method: 'GET' }, 5000);

    const endTime = performance.now();
    const duration = Math.round(endTime - startTime);

    if (!response.ok) {
      return { success: false, message: `HTTP ${response.status}` };
    }

    return { success: true, message: `Label Server reachable (${duration}ms)` };
  };

  testRecoveryServerPing = async (): Promise<{ success: boolean; message?: string; details?: string }> => {
    const startTime = performance.now();
    const url = `${FILE_RECOVERY_HTTPS_URL()}/ping`;
    const response = await this.fetchWithTimeout(url, { method: 'GET' }, 5000);

    const endTime = performance.now();
    const duration = Math.round(endTime - startTime);

    if (!response.ok) {
      return { success: false, message: `HTTP ${response.status}` };
    }

    return { success: true, message: `Recovery Server reachable (${duration}ms)` };
  };

  // ============ Render Method ============

  render() {
    const testsArray = Array.from(this.state.tests.values());

    return (
      <Dialog open={true} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <b>Internet Connectivity Test</b>
            {this.state.running && this.state.currentTestElapsedTime > 0 && (
              <Typography variant="body2" color="textSecondary">
                Elapsed: {this.state.currentTestElapsedTime.toFixed(1)}s
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} size={12}>
            {testsArray.map((test) => (
              <Grid key={test.id} size={12}>
                <Box display="flex" alignItems="center" style={{ padding: '8px 0' }}>
                  <Box width="40px" display="flex" justifyContent="center" alignItems="center">
                    {test.status === 'pending' && this.state.running && <CircularProgress size={24} />}
                    {test.status === 'success' && <CheckCircleIcon style={{ color: 'green', fontSize: 28 }} />}
                    {test.status === 'failure' && <CancelIcon style={{ color: 'red', fontSize: 28 }} />}
                  </Box>
                  <Box flex={1} marginLeft={2}>
                    <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                      {test.name}
                    </Typography>
                    {test.message && (
                      <Typography variant="body2" color={test.status === 'failure' ? 'error' : 'textSecondary'}>
                        {test.message}
                      </Typography>
                    )}
                    {test.details && (
                      <Typography
                        variant="caption"
                        component="pre"
                        style={{
                          marginTop: 4,
                          padding: 8,
                          backgroundColor: '#f5f5f5',
                          borderRadius: 4,
                          fontSize: '0.75rem',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {test.details}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions style={{ padding: 20 }}>
          <Button variant="outlined" onClick={this.props.onClose} disabled={this.state.running}>
            Close
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              // Reset all tests to pending
              const tests = new Map(this.state.tests);
              tests.forEach((test, id) => {
                tests.set(id, { ...test, status: 'pending' });
              });
              this.setState({ tests }, () => this.runTests());
            }}
            disabled={this.state.running}
          >
            Run Tests Again
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}
